import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Plug,
  CheckCircle2,
  AlertCircle,
  XCircle,
} from "lucide-react";

/* ───────── Myntra Partner API integration card ─────────
 * Lives inside the Profile page. Seller pastes their Myntra
 * `secret_key`, `merchant_id` and `partner_email` (the trio Myntra
 * shares when they onboard your brand to the Partner Integration
 * Layer), we call POST /seller/myntra/connect → Myntra responds
 * with an access_token (30-day TTL) + refresh_token, which we store.
 *
 * From this point the seller sees a "📤 Push to Myntra" button on the
 * Auto Listing page after every Excel generation.
 */
export default function MyntraIntegrationCard() {
  const [status, setStatus] = useState(null); // { connected, merchant_id, ... }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ secret_key: "", merchant_id: "", partner_email: "" });

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get("/seller/myntra/status");
      setStatus(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load Myntra status"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function onConnect(e) {
    e.preventDefault();
    if (!form.secret_key || !form.merchant_id || !form.partner_email) {
      toast.error("All 3 fields are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/seller/myntra/connect", form);
      setStatus(data);
      setForm({ secret_key: "", merchant_id: "", partner_email: "" });
      toast.success("Connected to Myntra ✓");
    } catch (e) {
      toast.error(errMsg(e, "Myntra connection failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onDisconnect() {
    if (!confirm("Disconnect Myntra? You'll need to re-enter your credentials to push catalogs.")) return;
    setBusy(true);
    try {
      await api.post("/seller/myntra/disconnect", {});
      toast.success("Myntra disconnected");
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, "Disconnect failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="mt-8 rounded-2xl border p-6"
      style={{ borderColor: "#E2E8F0", background: "#FFFFFF" }}
      data-testid="myntra-integration-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Plug size={16} style={{ color: "#FF3F6C" }} />
        <div className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: "#FF3F6C" }}>
          Myntra Partner API
        </div>
        {status?.connected && (
          <span
            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{ background: "#DCFCE7", color: "#065F46" }}
            data-testid="myntra-status-connected"
          >
            <CheckCircle2 size={11} /> Connected
          </span>
        )}
        {!loading && !status?.connected && (
          <span
            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{ background: "#FEF3C7", color: "#92400E" }}
            data-testid="myntra-status-disconnected"
          >
            <AlertCircle size={11} /> Not connected
          </span>
        )}
      </div>

      <p className="text-sm mb-4" style={{ color: "#535665" }}>
        Connect your Myntra Partner credentials to push generated catalogs
        directly to Myntra with one click — no more manual portal uploads.
        Get your <code style={{ background: "#F1F5F9", padding: "1px 4px", borderRadius: 4 }}>secret_key</code>{" "}
        and <code style={{ background: "#F1F5F9", padding: "1px 4px", borderRadius: 4 }}>merchant_id</code>{" "}
        from your Myntra Partner Console.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm" style={{ color: "#94A3B8" }}>
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : status?.connected ? (
        <ConnectedView status={status} onDisconnect={onDisconnect} busy={busy} />
      ) : (
        <ConnectForm form={form} setForm={setForm} onConnect={onConnect} busy={busy} />
      )}
    </div>
  );
}

function ConnectForm({ form, setForm, onConnect, busy }) {
  return (
    <form onSubmit={onConnect} className="space-y-3">
      <div className="grid grid-cols-1 gap-3">
        <Field
          label="Secret Key"
          value={form.secret_key}
          onChange={(v) => setForm({ ...form, secret_key: v })}
          placeholder="Provided by Myntra"
          testId="myntra-secret-key-input"
          type="password"
        />
        <Field
          label="Merchant ID"
          value={form.merchant_id}
          onChange={(v) => setForm({ ...form, merchant_id: v })}
          placeholder="e.g. MYN12345"
          testId="myntra-merchant-id-input"
        />
        <Field
          label="Partner Email"
          value={form.partner_email}
          onChange={(v) => setForm({ ...form, partner_email: v })}
          placeholder="catalog@yourbrand.com"
          testId="myntra-partner-email-input"
          type="email"
        />
      </div>
      <div className="flex items-center justify-end pt-1">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-bold disabled:opacity-50"
          style={{ background: "#FF3F6C", color: "#FFFFFF" }}
          data-testid="myntra-connect-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />}
          {busy ? "Connecting…" : "Connect to Myntra"}
        </button>
      </div>
    </form>
  );
}

function ConnectedView({ status, onDisconnect, busy }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Merchant ID" value={status.merchant_id || "—"} />
        <Stat label="Partner Email" value={status.partner_email || "—"} />
        <Stat
          label="Token Expires In"
          value={
            status.token_days_left == null
              ? "—"
              : `${status.token_days_left} day${status.token_days_left === 1 ? "" : "s"}`
          }
          accent={status.token_days_left != null && status.token_days_left < 5 ? "warn" : "ok"}
        />
      </div>
      {status.last_push_at && (
        <div className="text-xs" style={{ color: "#64748B" }}>
          Last catalog push: {new Date(status.last_push_at).toLocaleString()}
        </div>
      )}
      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={onDisconnect}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold border disabled:opacity-50"
          style={{ borderColor: "#FCA5A5", color: "#B91C1C", background: "#FFFFFF" }}
          data-testid="myntra-disconnect-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
          Disconnect Myntra
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#475569" }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-1 px-3 py-2 rounded-md border text-sm"
        style={{ borderColor: "#CBD5E1", color: "#0F172A", background: "#FFFFFF" }}
        data-testid={testId}
      />
    </div>
  );
}

function Stat({ label, value, accent = "neutral" }) {
  const colors = {
    neutral: { bg: "#F8FAFC", fg: "#0F172A" },
    ok: { bg: "#ECFDF5", fg: "#065F46" },
    warn: { bg: "#FEF3C7", fg: "#92400E" },
  };
  const c = colors[accent] || colors.neutral;
  return (
    <div className="rounded-lg p-3 border" style={{ background: c.bg, borderColor: "#E2E8F0" }}>
      <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#64748B" }}>
        {label}
      </div>
      <div className="text-sm font-bold mt-0.5" style={{ color: c.fg, wordBreak: "break-all" }}>
        {value}
      </div>
    </div>
  );
}
